<div class="property-lanes" role="group" [attr.aria-label]="ariaLabel()">
  <div class="property-lanes__scroller" #scroller>
    <div class="property-lanes__canvas" [style.--pl-track-width.px]="trackWidthPx()">

      @for (group of renderGroups(); track group.key; let groupIndex = $index) {
        <div class="property-lanes__group" [class.property-lanes__group--muted]="group.muted">
          <div class="property-lanes__group-header">
            <span class="property-lanes__group-dot" [style.background]="group.color || 'var(--amber)'" aria-hidden="true"></span>
            <span class="property-lanes__group-title">{{ group.label }}</span>
            @if (group.hint) {
              <span class="property-lanes__group-hint">{{ group.hint }}</span>
            }
            @if (group.collapsible) {
              <button type="button" class="property-lanes__group-toggle"
                      [attr.aria-expanded]="!group.collapsed"
                      [attr.aria-label]="group.toggleLabel"
                      [attr.title]="group.toggleLabel"
                      [attr.data-test-id]="'property-lanes-toggle-' + group.key"
                      (click)="onGroupToggle(group.key)">
                <i class="fas" [class.fa-chevron-down]="group.collapsed" [class.fa-chevron-up]="!group.collapsed" aria-hidden="true"></i>
              </button>
            }
          </div>

          <!-- Liniaal per groep (de eerste blijft bovenaan plakken); overal versleepbaar. -->
          <div class="property-lanes__row property-lanes__row--ruler" [class.property-lanes__row--ruler-sticky]="groupIndex === 0">
            <div class="property-lanes__labels property-lanes__labels--ruler"></div>
            <svg class="property-lanes__ruler"
                 [attr.width]="trackWidthPx()"
                 [attr.height]="rulerHeight"
                 [class.property-lanes__ruler--draggable]="markerDraggable()"
                 (pointerdown)="onTrackPointerDown($event)"
                 (pointermove)="onTrackPointerMove($event)"
                 (pointerup)="onTrackPointerUp($event)"
                 (pointercancel)="onTrackPointerCancel($event)">
              @for (tick of ticks(); track tick.xPx) {
                <line class="pl-tick" [class.pl-tick--major]="tick.major"
                      [attr.x1]="tick.xPx" [attr.x2]="tick.xPx"
                      [attr.y1]="tick.major ? rulerHeight - 9 : rulerHeight - 5" [attr.y2]="rulerHeight" />
                <text class="pl-tick-label" [class.pl-tick-label--major]="tick.major" [attr.x]="tick.xPx + 3" [attr.y]="rulerHeight - 11">{{ tick.label }}</text>
              }
              <g class="pl-anchor">
                <line [attr.x1]="anchorXPx()" [attr.x2]="anchorXPx()" y1="0" [attr.y2]="rulerHeight" />
                @if (anchorLabel()) {
                  <g class="pl-pill pl-pill--anchor" [attr.transform]="'translate(' + anchorXPx() + ',1)'">
                    <rect x="-24" y="0" width="48" height="16" rx="8" />
                    <text x="0" y="11.5" text-anchor="middle">{{ anchorLabel() }}</text>
                  </g>
                }
              </g>
              @if (markerXPx() !== undefined) {
                <g class="pl-marker" [class.pl-marker--draggable]="markerDraggable()">
                  <line [attr.x1]="markerXPx()" [attr.x2]="markerXPx()" y1="0" [attr.y2]="rulerHeight" />
                  @if (markerLabel()) {
                    <g class="pl-pill pl-pill--marker" [attr.transform]="'translate(' + markerXPx() + ',1)'">
                      <rect x="-30" y="0" width="60" height="16" rx="8" />
                      <text x="0" y="11.5" text-anchor="middle">{{ markerLabel() }}</text>
                    </g>
                  }
                </g>
              }
            </svg>
          </div>

          <div class="property-lanes__row">
            <div class="property-lanes__labels">
              @for (lane of group.lanes; track lane.key) {
                <div class="property-lanes__label" [style.height.px]="lane.heightPx" [attr.title]="lane.label">{{ lane.label }}</div>
              }
            </div>
            <svg class="property-lanes__track"
                 [attr.width]="trackWidthPx()"
                 [attr.height]="group.heightPx"
                 (pointerdown)="onTrackPointerDown($event)"
                 (pointermove)="onTrackPointerMove($event)"
                 (pointerup)="onTrackPointerUp($event)"
                 (pointercancel)="onTrackPointerCancel($event)">
              @for (lane of group.lanes; track lane.key) {
                <g [attr.transform]="'translate(0,' + lane.yPx + ')'">
                  <line class="pl-lane-line" x1="0" [attr.x2]="trackWidthPx()" [attr.y1]="lane.heightPx" [attr.y2]="lane.heightPx" />
                  @for (item of lane.blocks; track item.block.fromMs + ':' + item.yPx) {
                    <g class="pl-block"
                       [class.pl-block--birth]="item.block.state === 'birth'"
                       [class.pl-block--same]="item.block.state === 'same'"
                       [class.pl-block--different]="item.block.state === 'different'"
                       [class.pl-block--accent]="item.fill != null"
                       tabindex="0" role="button"
                       [attr.aria-label]="item.block.title"
                       (click)="onBlockClick(group.key, lane.key, item.block, $event)"
                       (keydown)="onBlockKeydown(group.key, lane.key, item.block, $event)">
                      <title>{{ item.block.title }}</title>
                      <rect [attr.x]="item.xPx + 0.5" [attr.y]="item.yPx"
                            [attr.width]="item.widthPx - 1" [attr.height]="item.heightPx" rx="4"
                            [attr.fill]="item.fill ?? null" [attr.stroke]="item.stroke ?? null" />
                      @if (item.parts.length > 0) {
                        <!-- Tekstkleur van accent-blokken als inline style: een attribuut verliest van de CSS-klasse (donker op donker). -->
                        <text class="pl-block__label" [class.pl-block__label--small]="item.heightPx < 18"
                              [attr.x]="item.textXPx" [attr.y]="item.textYPx" [style.fill]="item.textFill ?? null">
                          @for (part of item.parts; track $index) {
                            @if (signOf(part)) {
                              <tspan [class]="'pl-sign pl-sign--' + part.tone">{{ signOf(part) }}</tspan>
                            }
                            <tspan [class]="'pl-part pl-part--' + part.tone">{{ restOf(part) }}</tspan>
                          }
                        </text>
                      }
                    </g>
                  }
                  @for (marker of lane.markers; track $index) {
                    <g class="pl-marker-glyph" [attr.transform]="'translate(' + marker.xPx + ',0)'">
                      <title>{{ marker.title }}</title>
                      <line x1="0" x2="0" [attr.y1]="lane.heightPx - 8" [attr.y2]="lane.heightPx" [attr.stroke]="marker.accent" />
                      <text x="0" [attr.y]="lane.heightPx / 2 + 2" text-anchor="middle" [attr.fill]="marker.accent">{{ marker.glyph }}</text>
                    </g>
                  }
                  @if (lane.hint && lane.blocks.length <= 1 && lane.markers.length === 0) {
                    <text class="pl-hint" [attr.x]="trackWidthPx() - 6" [attr.y]="lane.heightPx / 2 + 4" text-anchor="end">{{ lane.hint }}</text>
                  }
                </g>
              }
              <line class="pl-anchor-line" [attr.x1]="anchorXPx()" [attr.x2]="anchorXPx()" y1="0" [attr.y2]="group.heightPx" />
              @if (markerXPx() !== undefined) {
                <line class="pl-marker-line" [attr.x1]="markerXPx()" [attr.x2]="markerXPx()" y1="0" [attr.y2]="group.heightPx" />
              }
            </svg>
          </div>
        </div>
      }
    </div>
  </div>
</div>
